// Ricerca per nome: helper condivisi fra la pagina Clienti, la pagina Lead e gli
// autocomplete. Nati il 3 set 2026 dal caso Èlan (studio dentistico, 2000+ pazienti):
// cercare "Mario Rossi" non trovava il paziente registrato come "Rossi Mario", perché
// la ricerca era un solo `ilike %Mario Rossi%` e nessuna delle due stringhe contiene
// l'altra. Per le persone fisiche l'ordine nome/cognome non è garantito, quindi la
// ricerca non può dipendere da come l'utente lo digita.

/**
 * PostgREST interpreta virgole e parentesi come sintassi dei filtri: un termine che le
 * contiene non "non trova nulla", cambia proprio il significato della query. Vengono
 * tolte, insieme ai jolly di LIKE (`%` e `_`), che altrimenti l'utente inietterebbe
 * senza saperlo scrivendoli nella casella di ricerca.
 */
export function sanitizeFilterValue(term: string): string {
  return term
    .replace(/[,()%_*\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Divide il testo cercato nelle sue parole, scartando quelle troppo corte (una lettera
 * sola farebbe combaciare mezza rubrica) e tenendone al massimo `max`: oltre, la query
 * cresce senza aggiungere precisione utile.
 */
export function searchTokens(term: string, max = 4): string[] {
  return sanitizeFilterValue(term)
    .split(' ')
    .filter((t) => t.length >= 2)
    .slice(0, max);
}

/**
 * Clausola PostgREST che richiede TUTTE le parole dentro `column`, in qualsiasi ordine
 * (`and(col.ilike.%a%,col.ilike.%b%)`, annidabile dentro un `or`).
 *
 * Ritorna null quando le parole utili sono meno di due: in quel caso il semplice ilike
 * sul termine intero fa già lo stesso lavoro, e aggiungere la clausola sarebbe rumore.
 */
export function allWordsClause(column: string, term: string): string | null {
  const tokens = searchTokens(term);
  if (tokens.length < 2) return null;
  return `and(${tokens.map((t) => `${column}.ilike.%${t}%`).join(',')})`;
}

/**
 * Versione lato client della stessa regola: `text` combacia se contiene TUTTE le parole
 * cercate, in qualsiasi ordine.
 *
 * Serve per forza insieme a `allWordsClause`: le liste Clienti e Lead rifiltrano in
 * memoria i risultati arrivati dal server, e un banale `includes(termine)` scarterebbe
 * proprio le righe che la query ha appena trovato ("Rossi Mario" non contiene la
 * stringa "Mario Rossi"), annullando la correzione lato server.
 *
 * Con una sola parola utile (o un carattere solo) si torna al semplice "contiene", così
 * la ricerca a una lettera continua a comportarsi come prima.
 */
export function matchesAllWords(text: string | null | undefined, term: string): boolean {
  if (!text) return false;
  const haystack = text.toLowerCase();
  const tokens = searchTokens(term);
  if (tokens.length === 0) {
    const semplice = sanitizeFilterValue(term).toLowerCase();
    return semplice ? haystack.includes(semplice) : false;
  }
  return tokens.every((t) => haystack.includes(t.toLowerCase()));
}
